import React, { useEffect, useState } from "react";
import { api, money, fileSrc } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { ScanLine, Loader2, X, Plus, Trash2, Receipt, ImagePlus } from "lucide-react";
import { SupplierPicker } from "./SupplierPicker";

const inputCls = "w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

export const TicketPurchaseNotes = ({ ticketId, onChange }) => {
  const { t } = useLang();
  const [notes, setNotes] = useState([]);
  const [stage, setStage] = useState("idle"); // idle | scanning | review
  const [supplier, setSupplier] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState([]);
  const [tax, setTax] = useState(0);
  const [photoUrl, setPhotoUrl] = useState("");
  const [receipts, setReceipts] = useState([]);

  const load = () => {
    api.get(`/tickets/${ticketId}`).then((tk) => setNotes(tk.purchase_notes || [])).catch(() => {});
    api.get(`/tickets/${ticketId}/receipts`).then(setReceipts).catch(() => {});
  };
  useEffect(() => { load(); }, [ticketId]); // eslint-disable-line

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStage("scanning");
    try {
      const up = await api.upload(file);
      setPhotoUrl(up.url || "");
      const res = await api.post(`/tickets/${ticketId}/scan-receipt`, { file_ids: [up.id] });
      setSupplier(res.supplier || "");
      if (res.date) setDate(res.date);
      setTax(res.tax || 0);
      setItems((res.items || []).map((i) => ({ ...i })));
      setStage("review");
      if (!res.items?.length) toast.message(t("noItemsFound"));
    } catch { toast.error(t("error")); setStage("idle"); }
    finally { e.target.value = ""; }
  };

  const upd = (i, k, v) => setItems(items.map((it, j) => j === i ? { ...it, [k]: k === "description" ? v : parseFloat(v) || 0 } : it));
  const addRow = () => setItems([...items, { description: "", quantity: 1, unit_price: 0 }]);
  const del = (i) => setItems(items.filter((_, j) => j !== i));
  const subtotal = items.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), 0);
  const grand = subtotal + (parseFloat(tax) || 0);

  const confirm = async () => {
    const clean = items.filter((i) => i.description.trim());
    if (clean.length === 0) return toast.error(t("noItemsFound"));
    try {
      await api.post(`/tickets/${ticketId}/purchase-note`, {
        supplier, purchased_date: new Date(date).toISOString(), tax: parseFloat(tax) || 0,
        items: clean, photos: photoUrl ? [photoUrl] : [],
      });
      toast.success(t("saved"));
      setStage("idle"); setItems([]); setSupplier(""); setTax(0); setPhotoUrl("");
      load(); onChange && onChange();
    } catch { toast.error(t("error")); }
  };

  const removeNote = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.del(`/tickets/${ticketId}/purchase-note/${id}`);
    load(); onChange && onChange();
  };

  return (
    <div className="border-t border-slate-200 pt-4" data-testid="purchase-notes-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-slate-500" />
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("purchaseNotes")}</span>
        </div>
        {stage === "scanning" ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-700"><Loader2 className="w-3.5 h-3.5 animate-spin" />{t("scanning")}</span>
        ) : (
          <label data-testid="add-purchase-note" className="inline-flex items-center gap-1.5 text-xs font-medium border border-violet-300 text-violet-700 rounded-lg px-3 py-1.5 hover:bg-violet-50 cursor-pointer">
            <ScanLine className="w-3.5 h-3.5" /> {t("addPurchaseNote")}
            <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={onFile} />
          </label>
        )}
      </div>

      {notes.length === 0 && <p className="text-xs text-slate-400 py-1">{t("noPurchaseNotes")}</p>}
      <div className="space-y-2">
        {notes.map((n) => (
          <div key={n.id} data-testid={`purchase-note-${n.id}`} className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900">{n.supplier || t("purchaseNote")}</div>
                <div className="text-[11px] text-slate-400">{(n.purchased_date || "").slice(0, 10)} · {n.items?.length || 0} {t("items")} · {t("byLabel")} {n.added_by}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-sm font-bold text-slate-900 tabular-nums">{money(n.total)}</span>
                <button data-testid={`del-purchase-note-${n.id}`} onClick={() => removeNote(n.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              {(n.photos || []).map((p, i) => (
                <a key={i} href={fileSrc(p)} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-md overflow-hidden border border-slate-200">
                  <img src={fileSrc(p)} alt="" className="w-full h-full object-cover" />
                </a>
              ))}
              <div className="text-[11px] text-slate-500">
                {(n.items || []).slice(0, 4).map((it, i) => (
                  <span key={i} className="inline-block mr-2">{it.description} <b className="text-slate-700">{money(it.total)}</b></span>
                ))}
                {n.items?.length > 4 && <span className="text-slate-400">+{n.items.length - 4}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {receipts.filter((r) => (r.photos || []).length > 0).length > 0 && (
        <div className="mt-3" data-testid="archived-receipts-section">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">{t("archivedReceipts")}</div>
          <div className="flex flex-wrap gap-2">
            {receipts.filter((r) => (r.photos || []).length > 0).map((r) => (
              <a key={r.id} href={fileSrc(r.photos[0])} target="_blank" rel="noreferrer" data-testid={`archived-receipt-${r.id}`}
                className="group border border-slate-200 rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow w-24">
                <img src={fileSrc(r.photos[0])} alt="" className="w-24 h-20 object-cover" />
                <div className="px-1.5 py-1">
                  <div className="text-[10px] font-semibold text-slate-700 truncate">{r.supplier || "—"}</div>
                  <div className="text-[10px] text-slate-400 tabular-nums">{(r.purchased_date || r.created_at || "").slice(0, 10)} · {money(r.total)}</div>
                  {r.po_number && <div className="text-[10px] text-violet-600 font-medium">{r.po_number}</div>}
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {stage === "review" && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setStage("idle")}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white">
              <h2 className="text-base font-semibold text-slate-900">{t("reviewReceipt")}</h2>
              <button onClick={() => setStage("idle")} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-5 space-y-3">
              {photoUrl && (
                <div className="flex items-center gap-2 text-xs text-slate-500"><ImagePlus className="w-4 h-4" /><img src={fileSrc(photoUrl)} alt="" className="w-16 h-16 rounded-md object-cover border border-slate-200" /></div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">{t("supplier")}</label><SupplierPicker value={supplier} onChange={setSupplier} testId="pn-supplier" /></div>
                <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">{t("purchasedOn")}</label><input data-testid="pn-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} /></div>
              </div>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input data-testid={`pn-desc-${i}`} value={it.description} onChange={(e) => upd(i, "description", e.target.value)} placeholder="Item" className={`${inputCls} col-span-6`} />
                    <input type="number" step="0.01" value={it.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} className={`${inputCls} col-span-2`} />
                    <input type="number" step="0.01" value={it.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} className={`${inputCls} col-span-3`} />
                    <button onClick={() => del(i)} className="col-span-1 flex justify-center text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={addRow} className="text-xs font-medium text-violet-700 inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" />{t("addItem")}</button>
              </div>
              <div className="border-t border-slate-200 pt-3 space-y-1.5">
                <div className="flex items-center justify-between text-sm"><span className="text-slate-500">{t("subtotal")}</span><span className="font-medium text-slate-700 tabular-nums">{money(subtotal)}</span></div>
                <div className="flex items-center justify-between text-sm"><span className="text-slate-500">{t("tax")}</span><input data-testid="pn-tax" type="number" step="0.01" value={tax} onChange={(e) => setTax(e.target.value)} className={`${inputCls} w-28 text-right`} /></div>
                <div className="flex items-center justify-between text-base font-bold text-slate-900 border-t border-slate-200 pt-1.5"><span>{t("total")}</span><span className="tabular-nums">{money(grand)}</span></div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-slate-200 sticky bottom-0 bg-white">
              <button onClick={() => setStage("idle")} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button data-testid="pn-save" onClick={confirm} className="rounded-lg px-4 py-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800">{t("save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
